// src/utils/critcastLaunch.js
// ============================================================================
// CRITCAST GO LIVE + DOWNLOAD HELPERS (design S7.6, S7.9, S8.2 W4; P14)
//
// Pure helpers and every member-facing string for the header Go Live button,
// the GoLiveModal and the /download page. No imports on purpose: the
// offline harnesses (scripts/test-golive-modal.mjs and
// scripts/test-download-copy.mjs) import this file straight into node and
// drive it with fake windows, documents and timers.
//
// Rules this file carries (S7.9 "Web rules"):
//   - size, sha256 and `signed` from DESKTOP_RELEASE are used only when
//     DESKTOP_RELEASE.version equals the version the public
//     getDesktopClientInfo callable returns (and the callable says
//     `available: true`); unknown counts as unsigned;
//   - DESKTOP_RELEASE.url is never a link: the download link is always the
//     callable's downloads.windows;
//   - localStorage.critcastSeenAt is a per-browser convenience, every read
//     and write sits in try/catch.
//
// Opening the app: launch() creates a hidden iframe whose src is
// critcash://go-live, inside the click (a user gesture), and waits up to
// 2.5 s for the window to blur or the page to hide. A blur means the browser
// handed off to the app or showed its "Open CritCast?" dialog ('handoff');
// no blur means no app answered ('timeout'). Any blur counts (the member
// clicking another window too); that only closes the modal, which is
// harmless. The hidden iframe is the Default because a top-level navigation
// to a scheme with no handler replaces the page with an error in Firefox,
// while the same navigation inside a hidden iframe stays invisible;
// Chromium browsers launch the protocol from an iframe created in a user
// gesture. A browser that refuses the iframe launch gets
// window.location.assign instead, chosen by a userAgent check: add its
// pattern to TOP_NAVIGATION_UA below once the smoke test (S8.3 step m2)
// shows the refusal. The list is empty until a browser is seen refusing.
// ============================================================================

// ---------------------------------------------------------------- constants

export const GO_LIVE_URL = 'critcash://go-live';
export const LAUNCH_TIMEOUT_MS = 2500;
export const SEEN_KEY = 'critcastSeenAt';
export const SEEN_TTL_MS = 30 * 24 * 60 * 60 * 1000; // Default: 30 days
export const SEEN_FUTURE_SKEW_MS = 5 * 60 * 1000;
export const COMPACT_HEADER_MAX_WIDTH = 768; // compact pill when width < 768
export const LAUNCH_FRAME_ID = 'critcast-launch-frame';

// Browsers that refuse a protocol launch from a hidden iframe and need a
// top-level window.location.assign instead (S7.6). Regular expressions over
// navigator.userAgent. Empty by Default: Edge, Chrome and Firefox on Windows
// launch from the iframe; the smoke test adds a pattern here if one refuses.
export const TOP_NAVIGATION_UA = [];

// Truth-table constants for W-06e (S8.1 "Download page numbers" row; S4.4
// decision table: 1080p60 from 4500 kbps measured, the 8000 kbps top
// setting from 8000 / 0.7 = 11429 kbps measured).
export const PAGE_MIN_UPLOAD_MBPS = 2.5;
export const PAGE_1080P60_MBPS = 4.5;
export const PAGE_TOP_BITRATE_MBPS = 11.5;

// Routes (App.js linking config): Download = /download,
// StreamingAccounts = /streaming, Login = /login.
export const ROUTE_DOWNLOAD = 'Download';
export const ROUTE_HUB = 'StreamingAccounts';
export const ROUTE_LOGIN = 'Login';
export const HUB_PATH = '/streaming';
export const LOGIN_PATH = '/login';

const SEMVER_RE = /^\d{1,4}\.\d{1,4}\.\d{1,6}(?:-[0-9A-Za-z.-]{1,32})?$/;
const SHA256_RE = /^[0-9a-f]{64}$/i;
const HTTPS_URL_RE = /^https:\/\/[^\s"'<>]+$/;

// ---------------------------------------------------------------- strings
// S7.6 G-01..G-12 (the modal and the header button), W-01..W-22 (the
// /download page) and S7.4 C-01..C-05 (the early build notice). ASCII only,
// no em dashes, product CritCast, company CritCash, Inc., money-free, no
// claims about other services; OBS and Streamlabs are named only as the
// member's own software. Strings with a value in them have a builder below.

export const G = Object.freeze({
  'G-01': 'Go Live',
  'G-02': 'Go live on CritCast',
  'G-03': 'Open CritCast',
  'G-03a': 'The Windows app sets up OBS and goes live for you.',
  'G-04': 'Download CritCast',
  'G-04a': 'Windows 10 or 11, 64-bit.',
  'G-05': 'Stream with your own software',
  'G-05a': 'Your stream key and server work with OBS, Streamlabs or any RTMP software.',
  'G-06': 'Opening CritCast...',
  'G-07': "CritCast didn't open. Download it, or stream with your own software.",
  'G-08': 'CritCast is a Windows app. On this device, stream with your own software.',
  'G-09': 'Not now',
  'G-10': 'CritCast is not signed yet. Windows warns you the first time you run the installer: click "More info", then "Run anyway". If your browser flags the download, choose Keep.',
  'G-11': 'Requirements, checksum and more',
  'G-12': 'Go Live',
});

// The /download page shows G-07's first sentence under W-12 on a timeout.
export const G07_FIRST_SENTENCE = "CritCast didn't open.";

export const C = Object.freeze({
  'C-01': 'Before you install',
  'C-02': 'CritCast is not signed yet, so Windows warns you the first time you run the installer.',
  'C-03': 'If you see "Windows protected your PC", click "More info", then "Run anyway". If your browser flags the download, choose Keep.',
  'C-04': 'Only install CritCast from this page.',
});

export const W = Object.freeze({
  'W-01': 'CritCast for Windows',
  'W-02': 'Sign in and go live. CritCast sets up OBS for you and keeps your stream key out of sight.',
  'W-03': 'Download for Windows',
  'W-05': 'What CritCast does',
  'W-05a': 'Sign in and go live. Your stream key goes straight into OBS. You never see it in CritCast or paste it.',
  'W-05b': 'Setup scan. Finds your graphics card and encoder, tests your upload, and applies the settings verified games require: a widescreen 16:9 canvas at 1080p or 720p, a fixed 60 or 30 fps, 2-second keyframes, B-frames off.',
  'W-05c': 'One home for your stream. Health, viewers, chat, simulcast and metrics in one window.',
  'W-05d': 'Clip that. Save the last moments of your stream to your account with one press. Private until you publish it.',
  'W-05e': 'Shared PC mode. Sign out and your key leaves the machine. Made for lounges and tournaments.',
  'W-05f': 'Updates itself.',
  'W-06': 'Requirements',
  'W-06a': 'Windows 10 (64-bit, version 1809 or later) or Windows 11.',
  'W-06b': "OBS Studio 30 or later. On an administrator account CritCast installs OBS for you if it is missing (about 160 MB); otherwise ask the PC's administrator to install it.",
  'W-06c': 'NVIDIA, AMD or Intel graphics with a hardware H.264 encoder recommended. Without one CritCast streams at 720p: 60 fps on a processor with 6 or more cores, otherwise 30 fps.',
  'W-06d': '8 GB memory, 500 MB free disk space, plus about 600 MB for OBS.',
  'W-06e': `Upload speed of ${PAGE_MIN_UPLOAD_MBPS} Mbps or more, as measured by CritCast's upload test. About ${PAGE_1080P60_MBPS} Mbps gives 1080p60; about ${PAGE_TOP_BITRATE_MBPS} Mbps gives the top setting of 8000 kbps (6000 kbps while simulcasting to Twitch).`,
  'W-06f': 'A CritCast account. Members are 18 and older.',
  'W-07': 'Coming soon',
  'W-08': "We couldn't load the download right now. Try again in a minute.",
  'W-09': 'CritCast is for Windows. Mac is not available yet.',
  'W-11': 'Already have CritCast?',
  'W-12': 'Open CritCast',
  'W-13': 'Questions? support@critcash.com',
  'W-14': 'Publisher: CritCash, Inc.',
  'W-21': 'Open the Streaming Hub',
  'W-22': 'Check for updates',
});

export const SUPPORT_EMAIL = 'support@critcash.com';

// Ordered lists the screens render.
export const W_FEATURE_IDS = Object.freeze(['W-05a', 'W-05b', 'W-05c', 'W-05d', 'W-05e', 'W-05f']);
export const W_REQUIREMENT_IDS = Object.freeze(['W-06a', 'W-06b', 'W-06c', 'W-06d', 'W-06e', 'W-06f']);

// G-04a with the version from the callable and the size from DESKTOP_RELEASE
// (size only when the versions match). "Windows 10 or 11, 64-bit. Version
// 2.0.0, 95 MB."
export function g04a(version, sizeText) {
  const base = G['G-04a'];
  if (!isVersionString(version)) return base;
  return sizeText ? `${base} Version ${version}, ${sizeText}.` : `${base} Version ${version}.`;
}

// W-04: "Version 2.0.0 for Windows 64-bit, 95 MB".
export function w04(version, sizeText) {
  if (!isVersionString(version)) return null;
  return sizeText
    ? `Version ${version} for Windows 64-bit, ${sizeText}`
    : `Version ${version} for Windows 64-bit`;
}

// W-04's release line: "Released Oct 20, 2026".
export function w04Released(dateText) {
  return dateText ? `Released ${dateText}` : null;
}

// W-20: "You're using CritCast 2.0.0".
export function w20(appVersion) {
  return isVersionString(appVersion) ? `You're using CritCast ${appVersion}` : "You're using CritCast";
}

// C-05: the checksum line. "SHA-256 of CritCast-Setup-2.0.0.exe: <hash>.
// Check it in PowerShell: Get-FileHash .\CritCast-Setup-2.0.0.exe"
export function c05(version, sha256) {
  if (!isVersionString(version) || !isSha256(sha256)) return null;
  const file = `CritCast-Setup-${version}.exe`;
  return `SHA-256 of ${file}: ${String(sha256).toLowerCase()}. Check it in PowerShell: Get-FileHash .\\${file}`;
}

// ---------------------------------------------------------------- small validators

export function isVersionString(v) {
  return typeof v === 'string' && SEMVER_RE.test(v);
}

export function isSha256(v) {
  return typeof v === 'string' && SHA256_RE.test(v);
}

export function isHttpsUrl(v) {
  return typeof v === 'string' && v.length <= 2048 && HTTPS_URL_RE.test(v);
}

// ---------------------------------------------------------------- the Go Live decision

// Header press -> what happens (S7.6 behaviour table):
//   'panel'   inside the desktop app: critcashDesktop.broadcast.open()
//   'mobile'  phone or tablet: the modal with G-08 and choice 3 only
//   'launch'  a remembered handoff: the modal opens in G-06 and tries the app
//   'choices' everything else: the three choices in order
// A logged-out visitor gets the same answer as a member (the download needs
// no account; signing in happens in the app).
export function goLiveTarget({ inDesktop, mobile, loggedIn, seenAt, nowMs } = {}) {
  void loggedIn;
  if (inDesktop === true) return 'panel';
  if (mobile === true) return 'mobile';
  const now = typeof nowMs === 'number' && Number.isFinite(nowMs) ? nowMs : Date.now();
  if (seenRecently(seenAt, now)) return 'launch';
  return 'choices';
}

// critcastSeenAt holds String(Date.now()) (what this file writes); an ISO
// date string is read too. True when the handoff was within the last 30 days
// (Default). A time in the future beyond a small clock skew is ignored.
export function seenRecently(stored, nowMs, ttlMs = SEEN_TTL_MS) {
  if (stored === null || stored === undefined) return false;
  const s = String(stored).trim();
  if (!s || s.length > 40) return false;
  let t;
  if (/^\d{10,16}$/.test(s)) t = Number(s);
  else t = Date.parse(s);
  if (!Number.isFinite(t)) return false;
  const now = typeof nowMs === 'number' && Number.isFinite(nowMs) ? nowMs : Date.now();
  if (t > now + SEEN_FUTURE_SKEW_MS) return false;
  return now - t <= ttlMs;
}

export function isCompactHeader(width) {
  return typeof width === 'number' && Number.isFinite(width) && width < COMPACT_HEADER_MAX_WIDTH;
}

// Header slot of the pill. Under 360 px the top row cannot fit the compact
// pill and the guest LOGIN button together (about 340 px of content on a
// 320 px phone), so the pill moves to the end of the row, after LOGIN and
// the wallet chips; the row still scrolls to it. 'first' everywhere else.
export const TINY_HEADER_MAX_WIDTH = 360;
export function goLiveHeaderSlot(width) {
  return typeof width === 'number' && Number.isFinite(width) && width < TINY_HEADER_MAX_WIDTH ? 'last' : 'first';
}

// What the modal does after a launch attempt (S7.6): a handoff closes the
// modal and remembers it; a timeout shows G-07 with choices 2 and 3 and
// forgets any remembered handoff.
export function afterLaunch(result) {
  if (result === 'handoff') return { close: true, view: null, seen: 'set' };
  return { close: false, view: 'nohandoff', seen: 'clear' };
}

// Which choices each modal view shows, in order (1 Open, 2 Download,
// 3 Own software).
export function choicesFor(view) {
  if (view === 'choices') return [1, 2, 3];
  if (view === 'nohandoff') return [2, 3];
  if (view === 'mobile') return [3];
  return [];
}

// The note line each view shows under the title.
export function noteFor(view) {
  if (view === 'launching') return G['G-06'];
  if (view === 'nohandoff') return G['G-07'];
  if (view === 'mobile') return G['G-08'];
  return null;
}

// ---------------------------------------------------------------- targets

// Choice 2: the installer URL from getDesktopClientInfo when one is
// published, else the Download page (which shows W-07 "Coming soon").
export function downloadTarget(info) {
  const url = info && info.available === true && info.downloads ? info.downloads.windows : null;
  if (isHttpsUrl(url)) return { kind: 'url', url };
  return { kind: 'route', route: ROUTE_DOWNLOAD };
}

// Choice 3: the Streaming Hub when signed in; Login with a return to the hub
// when signed out (LoginScreen honours params.next from its allowlist).
export function ownSoftwareTarget({ signedIn } = {}) {
  if (signedIn === true) return { route: ROUTE_HUB, params: undefined, path: HUB_PATH };
  return { route: ROUTE_LOGIN, params: { next: ROUTE_HUB }, path: LOGIN_PATH };
}

// ---------------------------------------------------------------- release facts

// True only when the callable has a published release and DESKTOP_RELEASE
// describes that same version.
export function versionsMatch(info, release) {
  return !!(
    info && release &&
    info.available === true &&
    isVersionString(release.version) &&
    info.version === release.version
  );
}

export function isSigned(info, release) {
  return versionsMatch(info, release) && release.signed === true;
}

export function showShaBlock(info, release) {
  return versionsMatch(info, release) && isSha256(release.sha256);
}

// The version the callable publishes, only when it says a build is out.
export function displayVersion(info) {
  return info && info.available === true && isVersionString(info.version) ? info.version : null;
}

// Decimal megabytes, as the requirements copy counts them (W-06b: the
// 159,809,104-byte OBS installer is "about 160 MB"). null = omit the size.
export function formatSize(bytes) {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes <= 0) return null;
  if (bytes < 1e6) return `${Math.max(1, Math.round(bytes / 1e3))} KB`;
  if (bytes < 1e9) return `${Math.round(bytes / 1e6)} MB`;
  return `${(bytes / 1e9).toFixed(1)} GB`;
}

export function sizeText(info, release) {
  return versionsMatch(info, release) ? formatSize(release.sizeBytes) : null;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "Oct 20, 2026" in Central time (the company's time zone); UTC parts when
// the runtime has no time zone data.
export function formatReleaseDate(iso) {
  if (typeof iso !== 'string' || !iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago', year: 'numeric', month: 'short', day: 'numeric',
    }).formatToParts(new Date(t));
    const get = (type) => (parts.find((p) => p.type === type) || {}).value;
    const m = get('month');
    const d = get('day');
    const y = get('year');
    if (m && d && y && MONTHS.includes(m)) return `${m} ${Number(d)}, ${y}`;
  } catch (e) {
    // fall through to UTC parts
  }
  const dt = new Date(t);
  return `${MONTHS[dt.getUTCMonth()]} ${dt.getUTCDate()}, ${dt.getUTCFullYear()}`;
}

export function isWindowsUA(ua) {
  return typeof ua === 'string' && /Windows/i.test(ua) && !/Windows Phone/i.test(ua);
}

// ---------------------------------------------------------------- view models

// The early build notices (G-10, W-10) describe an installer the member is
// about to run, so they show only while one is published (the callable
// gives a download URL) and it is not signed. Hosting ships before the
// installer (P15 steps 5 and 7): until then the modal's Download goes to the
// "Coming soon" page and neither surface warns about a build that is not
// there.
export function showUnsignedNotice(info, release) {
  return downloadTarget(info).kind === 'url' && !isSigned(info, release);
}

// Choice 2 at the press. shownInfo = the answer the modal was showing when
// the member pressed (null while it loaded), latestInfo = the answer the
// press waited for. The installer URL only when G-10 was already on screen
// for an unsigned build, or none was needed; a build that turned out to be
// published and unsigned after the press goes to the Download page, whose
// early build box explains the Windows warning first (S7.6: the member meets
// SmartScreen with the explanation already read).
export function downloadPressTarget({ shownInfo, latestInfo, release } = {}) {
  const target = downloadTarget(latestInfo);
  if (target.kind !== 'url') return target;
  if (showUnsignedNotice(latestInfo, release) && !showUnsignedNotice(shownInfo, release)) {
    return { kind: 'route', route: ROUTE_DOWNLOAD };
  }
  return target;
}

// The Go Live modal for one view. `info` is the callable's answer (or null
// while it loads or when it failed), `release` is DESKTOP_RELEASE.
export function goLiveModalModel({ view, info, release } = {}) {
  const v = displayVersion(info);
  return {
    title: G['G-02'],
    note: noteFor(view),
    choices: choicesFor(view),
    busy: view === 'launching',
    downloadDetail: g04a(v, sizeText(info, release)),
    unsignedNote: showUnsignedNotice(info, release) ? G['G-10'] : null,
    download: downloadTarget(info),
    closeLabel: G['G-09'],
  };
}

// The /download page outside the app.
export function downloadPageModel({ info, release, loading, loadError, userAgent } = {}) {
  const target = loading ? { kind: 'route', route: ROUTE_DOWNLOAD } : downloadTarget(info);
  const live = target.kind === 'url';
  const v = live ? displayVersion(info) : null;
  const signed = isSigned(info, release);
  return {
    loading: !!loading,
    buttonLabel: live ? W['W-03'] : W['W-07'],
    buttonEnabled: live,
    downloadUrl: live ? target.url : null,
    versionLine: v ? w04(v, sizeText(info, release)) : null,
    releasedLine: v ? w04Released(formatReleaseDate(info.releaseDate)) : null,
    loadErrorLine: !loading && loadError ? W['W-08'] : null,
    nonWindowsLine: isWindowsUA(userAgent || '') ? null : W['W-09'],
    showEarlyBuild: live && !signed,
    publisherLine: signed ? W['W-14'] : null,
    shaLine: showShaBlock(info, release) ? c05(release.version, release.sha256) : null,
  };
}

// ---------------------------------------------------------------- callable cache

// One getDesktopClientInfo call per page session (S7.6), shared by the modal
// and the /download page. A failure is not cached, so the next open retries.
export function createInfoLoader(fetchInfo) {
  let pending = null;
  return function loadInfo() {
    if (!pending) {
      pending = Promise.resolve()
        .then(() => fetchInfo())
        .catch((err) => {
          pending = null;
          throw err;
        });
    }
    return pending;
  };
}

// ---------------------------------------------------------------- localStorage

export function getStorage(win) {
  try {
    const w = win || (typeof window !== 'undefined' ? window : undefined);
    return w && w.localStorage ? w.localStorage : null;
  } catch (e) {
    return null;
  }
}

export function readSeenAt(storage) {
  try {
    return storage ? storage.getItem(SEEN_KEY) : null;
  } catch (e) {
    return null;
  }
}

export function writeSeenAt(storage, nowMs) {
  try {
    if (storage) storage.setItem(SEEN_KEY, String(typeof nowMs === 'number' ? nowMs : Date.now()));
  } catch (e) {
    // A blocked localStorage only means the question is asked again next time.
  }
}

export function clearSeenAt(storage) {
  try {
    if (storage) storage.removeItem(SEEN_KEY);
  } catch (e) {
    // ignore
  }
}

// Applies afterLaunch()'s `seen` verdict to localStorage.
export function recordLaunchResult(result, storage, nowMs) {
  const verdict = afterLaunch(result);
  if (verdict.seen === 'set') writeSeenAt(storage, nowMs);
  else if (verdict.seen === 'clear') clearSeenAt(storage);
  return verdict;
}

// ---------------------------------------------------------------- the launch

export function prefersTopNavigation(userAgent, list = TOP_NAVIGATION_UA) {
  if (typeof userAgent !== 'string' || !Array.isArray(list)) return false;
  return list.some((re) => re instanceof RegExp && re.test(userAgent));
}

// Sends critcash://go-live: a hidden iframe created now (call this inside
// the click handler), or window.location.assign for a browser listed in
// TOP_NAVIGATION_UA or when the iframe cannot be made. Returns 'iframe',
// 'top' or 'none'.
export function fireDeepLink({ win, doc, userAgent } = {}) {
  const ua = typeof userAgent === 'string'
    ? userAgent
    : (win && win.navigator && typeof win.navigator.userAgent === 'string' ? win.navigator.userAgent : '');
  const topNav = () => {
    try {
      if (win && win.location && typeof win.location.assign === 'function') {
        win.location.assign(GO_LIVE_URL);
        return 'top';
      }
    } catch (e) {
      // nothing else to try
    }
    return 'none';
  };
  if (prefersTopNavigation(ua)) return topNav();
  try {
    if (!doc || typeof doc.createElement !== 'function') return topNav();
    const parent = doc.body || doc.documentElement;
    if (!parent || typeof parent.appendChild !== 'function') return topNav();
    const old = typeof doc.getElementById === 'function' ? doc.getElementById(LAUNCH_FRAME_ID) : null;
    if (old && old.parentNode && typeof old.parentNode.removeChild === 'function') old.parentNode.removeChild(old);
    const frame = doc.createElement('iframe');
    frame.id = LAUNCH_FRAME_ID;
    if (typeof frame.setAttribute === 'function') {
      frame.setAttribute('aria-hidden', 'true');
      frame.setAttribute('tabindex', '-1');
      frame.setAttribute('title', 'CritCast');
    }
    if (frame.style) {
      frame.style.display = 'none';
      frame.style.width = '0';
      frame.style.height = '0';
      frame.style.border = '0';
    }
    parent.appendChild(frame);
    frame.src = GO_LIVE_URL;
    return 'iframe';
  } catch (e) {
    return topNav();
  }
}

// launch({ timeoutMs: 2500 }) resolves 'handoff' on a window blur or a
// document hide before the timer, else 'timeout' at timeoutMs. The listeners
// go on before the link fires so a synchronous blur is caught. `win`, `doc`,
// `setTimer`, `clearTimer` and `userAgent` are injectable for the harness.
export function launch(opts = {}) {
  const win = opts.win || (typeof window !== 'undefined' ? window : undefined);
  const doc = opts.doc || (typeof document !== 'undefined' ? document : undefined);
  const timeoutMs = typeof opts.timeoutMs === 'number' && opts.timeoutMs > 0 ? opts.timeoutMs : LAUNCH_TIMEOUT_MS;
  const setTimer = typeof opts.setTimer === 'function' ? opts.setTimer : (fn, ms) => setTimeout(fn, ms);
  const clearTimer = typeof opts.clearTimer === 'function' ? opts.clearTimer : (id) => clearTimeout(id);

  return new Promise((resolve) => {
    if (!win || typeof win.addEventListener !== 'function') {
      resolve('timeout');
      return;
    }
    let done = false;
    let timer = null;
    const onBlur = () => finish('handoff');
    const onVisibility = () => {
      if (doc && (doc.visibilityState === 'hidden' || doc.hidden === true)) finish('handoff');
    };
    function cleanup() {
      try { win.removeEventListener('blur', onBlur); } catch (e) { /* ignore */ }
      try { if (doc && typeof doc.removeEventListener === 'function') doc.removeEventListener('visibilitychange', onVisibility); } catch (e) { /* ignore */ }
      if (timer !== null) {
        try { clearTimer(timer); } catch (e) { /* ignore */ }
        timer = null;
      }
    }
    function finish(result) {
      if (done) return;
      done = true;
      cleanup();
      resolve(result);
    }
    win.addEventListener('blur', onBlur);
    if (doc && typeof doc.addEventListener === 'function') doc.addEventListener('visibilitychange', onVisibility);
    timer = setTimer(() => finish('timeout'), timeoutMs);
    fireDeepLink({ win, doc, userAgent: opts.userAgent });
  });
}

// ---------------------------------------------------------------- the desktop bridge

// Inside the app the header Go Live expands the Broadcast panel through the
// site preload (P14). Resolves 'opened', 'missing' (an older app without the
// method) or 'failed' (the call threw); the caller opens the Streaming Hub
// on anything but 'opened'.
export function openBroadcastPanel(win) {
  try {
    const w = win || (typeof window !== 'undefined' ? window : undefined);
    const fn = w && w.critcashDesktop && w.critcashDesktop.broadcast && w.critcashDesktop.broadcast.open;
    if (typeof fn !== 'function') return Promise.resolve('missing');
    return Promise.resolve()
      .then(() => fn.call(w.critcashDesktop.broadcast))
      .then(() => 'opened', () => 'failed');
  } catch (e) {
    return Promise.resolve('failed');
  }
}

// The in-app /download block: the app's own version (W-20).
export function readAppVersion(win) {
  try {
    const w = win || (typeof window !== 'undefined' ? window : undefined);
    const fn = w && w.critcashDesktop && w.critcashDesktop.getPlatformInfo;
    if (typeof fn !== 'function') return Promise.resolve(null);
    return Promise.resolve()
      .then(() => fn.call(w.critcashDesktop))
      .then((info) => (info && isVersionString(info.appVersion) ? info.appVersion : null), () => null);
  } catch (e) {
    return Promise.resolve(null);
  }
}

// W-22: asks the app to check for updates (the app shows the result in its
// own panel). Resolves true when the call was made.
export function checkAppUpdates(win) {
  try {
    const w = win || (typeof window !== 'undefined' ? window : undefined);
    const updates = w && w.critcashDesktop && w.critcashDesktop.updates;
    const fn = updates && updates.check;
    if (typeof fn !== 'function') return Promise.resolve(false);
    return Promise.resolve()
      .then(() => fn.call(updates))
      .then(() => true, () => false);
  } catch (e) {
    return Promise.resolve(false);
  }
}
